import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ScanResult = ({ result, onClear, onManualOverride }) => {
  if (!result) return null;

  const getResultIcon = () => {
    if (!result?.isValid) return { name: 'XCircle', color: 'text-error' };
    
    switch (result?.type) {
      case 'entry':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'upgrade':
        return { name: 'ArrowUp', color: 'text-accent' };
      case 'merchandise':
        return { name: 'Package', color: 'text-primary' };
      case 'member':
        return { name: 'Star', color: 'text-warning' };
      default:
        return { name: 'CheckCircle', color: 'text-success' };
    }
  };

  const getResultTitle = () => {
    if (!result?.isValid) return 'Invalid Scan';
    
    switch (result?.type) {
      case 'entry':
        return 'Entry Approved';
      case 'upgrade':
        return 'Upgrade Validated';
      case 'merchandise':
        return 'Item Pickup';
      case 'member':
        return 'Member Points Added';
      default:
        return 'Scan Successful';
    }
  };

  const getResultMessage = () => {
    if (!result?.isValid) {
      return 'This QR code is not valid or has already been used.';
    }
    
    switch (result?.type) {
      case 'entry':
        return `Welcome to ${result?.data?.eventName}`;
      case 'upgrade':
        return `${result?.data?.upgradeType} access granted`;
      case 'merchandise':
        return `${result?.data?.itemName} ready for pickup`;
      case 'member':
        return `+${result?.data?.memberPoints} points added to account`;
      default:
        return 'Scan processed successfully';
    }
  };

  const icon = getResultIcon();

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6 animate-fade-in">
      {/* Result Header */}
      <div className="flex items-center space-x-4 mb-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
          result?.isValid ? 'bg-success' : 'bg-error'
        }`}>
          <Icon name={icon?.name} size={24} color="white" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-foreground">
            {getResultTitle()}
          </h3>
          <p className="text-sm text-muted-foreground">
            {result?.timestamp?.toLocaleTimeString()}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          iconName="X"
          onClick={onClear}
          className="text-muted-foreground hover:text-foreground"
        />
      </div>
      {/* Result Message */}
      <div className="mb-4">
        <p className="text-foreground font-medium mb-2">
          {getResultMessage()}
        </p>
      </div>
      {/* Scan Details */}
      <div className="space-y-3 text-sm">
        <div className="flex justify-between items-center py-2 border-b border-border">
          <span className="text-muted-foreground">Scan ID:</span>
          <span className="font-mono text-foreground">{result?.id}</span>
        </div>
        
        {result?.data?.ticketId && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Ticket ID:</span>
            <span className="font-mono text-foreground">{result?.data?.ticketId}</span>
          </div>
        )}
        
        {result?.data?.attendeeName && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Attendee:</span>
            <span className="text-foreground">{result?.data?.attendeeName}</span>
          </div>
        )}
        
        {result?.data?.eventName && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Event:</span>
            <span className="text-foreground">{result?.data?.eventName}</span>
          </div>
        )}
        
        {result?.data?.seatSection && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Section:</span>
            <span className="text-foreground">{result?.data?.seatSection}</span>
          </div>
        )}
        
        {result?.data?.upgradeType && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Upgrade:</span>
            <span className="text-foreground">{result?.data?.upgradeType}</span>
          </div>
        )}
        
        {result?.data?.itemName && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Item:</span>
            <span className="text-foreground">{result?.data?.itemName}</span>
          </div>
        )}
        
        {result?.data?.memberPoints && (
          <div className="flex justify-between items-center py-2 border-b border-border">
            <span className="text-muted-foreground">Points Added:</span>
            <span className="text-accent font-semibold">+{result?.data?.memberPoints}</span>
          </div>
        )}
        
        <div className="flex justify-between items-center py-2">
          <span className="text-muted-foreground">Status:</span>
          <span className={`font-medium ${result?.isValid ? 'text-success' : 'text-error'}`}>
            {result?.isValid ? 'Valid' : 'Invalid'}
          </span>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex space-x-3 mt-6">
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          className="flex-1"
        >
          Clear Result
        </Button>
        
        {!result?.isValid && (
          <Button
            variant="warning"
            size="sm"
            onClick={() => onManualOverride(result)}
            iconName="AlertTriangle"
            className="flex-1"
          >
            Manual Override
          </Button>
        )}
      </div>
    </div>
  );
};

export default ScanResult;